/*
 * horizonScheduler.js — the Horizon scheduler (frontend copy, WR-024).
 *
 * This file is generated by backend/scripts/sync-horizon-scheduler.js from
 * backend/utils/horizonScheduler.js. Do not edit it directly; change the backend
 * file and re-run the script. The --check mode verifies the copy is in sync.
 */
/*
 * utils/horizonScheduler.js — the Horizon scheduler (WR-024).
 *
 * A Horizon is a constraint-scheduled timeline over a project's tasks. It
 * replaces a spreadsheet (timeline_gantt_v14.xlsx) whose rows each carry a
 * resource lane, a duration in weeks, an optional predecessor and an optional
 * "not before" week, and whose Start column is one formula:
 *
 *     MAX(lane's previous end, predecessor's end, not-before − 1) + 1
 *
 * This module is that formula, generalised: several lanes per row (the
 * sheet's "Both"), several predecessors, parallel lanes that never queue
 * (the sheet's suppliers), pinned starts, and milestones.
 *
 * ── Why it is pure ────────────────────────────────────────────────────
 *
 * The server stores the computed start/end on every save so that queries,
 * MCP and every other consumer read the schedule the UI shows; the browser
 * recomputes the same schedule instantly on every edit. Both must agree to
 * the week, so there is exactly one implementation and it touches nothing
 * but its argument: no database, no clock, no environment. The frontend
 * runs a copy of this file against the same test vector
 * (test/fixtures/horizon-schedule.fixture.json).
 *
 * ── Two rules the sheet has that its description does not ────────────
 *
 *   · not-before is INCLUSIVE. The sheet puts N($F)−1 inside the MAX and
 *     adds 1 afterwards, so "not before week 4" can start in week 4.
 *   · a milestone gates the START of its week. The sheet's Engine row is
 *     G22 = G21: it begins in the week Financing is secured, not the week
 *     after. A task predecessor, by contrast, frees its successor the week
 *     after it ends.
 *
 * Week numbers are 1-based integers throughout; week 1 starts on the
 * Horizon's anchor Monday.
 *
 * The identical implementation lives at frontend/src/components/horizonScheduler.js.
 * Edit this file, then re-run `node backend/scripts/sync-horizon-scheduler.js`; both
 * sides run the same fixture (`test/fixtures/horizon-schedule.fixture.json`).
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

const isSet = v => v !== null && v !== undefined;
const isWeek = v => Number.isInteger(v) && v >= 1;
const ids = list => (Array.isArray(list) ? list : []).map(String);

/**
 * Entries and milestones as one list in processing order.
 *
 * They share a single `order` sequence because a milestone sits between rows
 * exactly like a task does, and "a predecessor must come earlier" has to
 * mean the same thing for both. Ties are broken entries-first, then by
 * array position, so the result never depends on sort stability.
 */
function sequence(horizon) {
  const entries = (horizon.entries || []).map((e, i) => ({ kind: 'entry', item: e, id: String(e.id), rank: i }));
  const milestones = (horizon.milestones || []).map((m, i) => ({ kind: 'milestone', item: m, id: String(m.id), rank: i }));
  return entries.concat(milestones).sort((a, b) =>
    (a.item.order - b.item.order)
    || ((a.kind === 'entry' ? 0 : 1) - (b.kind === 'entry' ? 0 : 1))
    || (a.rank - b.rank));
}

/**
 * Lay out a Horizon.
 *
 * Never throws: anything malformed is ignored and reported in `diagnostics`,
 * because the view must keep rendering a plan someone is half-way through
 * editing. validate() is the strict counterpart the API answers 400 from.
 *
 * @returns {{ entries: {id, start, end}[], milestones: {id, week}[],
 *             diagnostics: {kind, id, ref}[], lastWeek: number }}
 */
function schedule(horizon) {
  const lanes = new Map((horizon.lanes || []).map(l => [String(l.id), l]));
  const order = sequence(horizon);
  const known = new Set(order.map(x => x.id));

  const done = new Map();      // id → { kind, end | week, taskState }
  const laneEnd = new Map();   // serial lane id → latest end scheduled on it
  const diagnostics = [];
  const out = { entries: [], milestones: [], diagnostics, lastWeek: 0 };

  /*
   * The predecessor gate: the latest week a row must wait out.
   *
   * A task predecessor contributes its end (the successor starts the week
   * after); a milestone contributes week − 1 (the successor may start IN
   * the milestone's week). A reference to the row itself or to a later row
   * is ignored, as the sheet's N($E)>=N($A) → 0 does, and reported.
   */
  function gate(id, after) {
    let latest = 0;
    for (const ref of ids(after)) {
      if (!known.has(ref)) {
        diagnostics.push({ kind: 'dependency-unknown', id, ref });
        continue;
      }
      const pred = done.get(ref);
      if (!pred) {
        diagnostics.push({ kind: 'dependency-not-earlier', id, ref });
        continue;
      }
      latest = Math.max(latest, pred.kind === 'milestone' ? pred.week - 1 : pred.end);
      // An archived or deleted task still holds its place in the plan —
      // its successors keep waiting for it — but the link is flagged so the
      // view can draw it as broken instead of pretending all is well.
      if (pred.taskState === 'archived' || pred.taskState === 'deleted') {
        diagnostics.push({ kind: `dependency-${pred.taskState}`, id, ref });
      }
    }
    return latest;
  }

  for (const { kind, item, id } of order) {
    if (kind === 'milestone') {
      // Milestones occupy no lane: a fixed week, or the start of the week
      // after their predecessors finish.
      const waited = gate(id, item.after);
      const week = isSet(item.week) ? item.week : waited + 1;
      done.set(id, { kind, week });
      out.milestones.push({ id, week });
      out.lastWeek = Math.max(out.lastWeek, week);
      continue;
    }

    // The lane rule: a serial lane runs one entry at a time, so this entry
    // waits for the latest end already on any lane it occupies (the sheet's
    // MAXIFS over the rows above with the same assignee). Parallel lanes
    // never queue. An entry on several lanes waits for, and then blocks,
    // all of them — the sheet's "Both".
    const serial = [];
    let laneFree = 0;
    for (const laneId of ids(item.lanes)) {
      const lane = lanes.get(laneId);
      if (!lane) {
        diagnostics.push({ kind: 'lane-unknown', id, ref: laneId });
        continue;
      }
      if (lane.capacity === 'parallel') continue;
      serial.push(laneId);
      laneFree = Math.max(laneFree, laneEnd.get(laneId) || 0);
    }

    const waited = gate(id, item.after);
    const notBefore = isSet(item.notBefore) ? item.notBefore : 1;

    // A pinned start overrides every rule above for this entry's own start…
    const start = isSet(item.pinnedStart)
      ? item.pinnedStart
      : Math.max(laneFree, waited, notBefore - 1, 0) + 1;
    const end = start + item.duration - 1;

    // …but the entry still occupies its lanes, so later rows queue behind it.
    for (const laneId of serial) laneEnd.set(laneId, Math.max(laneEnd.get(laneId) || 0, end));

    done.set(id, { kind, end, taskState: item.taskState });
    out.entries.push({ id, start, end });
    out.lastWeek = Math.max(out.lastWeek, end);
  }

  return out;
}

/**
 * Every reason the API should refuse to store this Horizon (it answers 400
 * with the list). Empty means valid.
 *
 * Cycles need no check of their own: a predecessor must come earlier in the
 * order, so a dependency can only point backwards and no loop can close.
 * Archived and deleted predecessors are not errors — the row stays, flagged,
 * until its owner removes it (see schedule()).
 */
function validate(horizon) {
  const errors = [];

  const laneIds = new Set();
  for (const lane of horizon.lanes || []) {
    laneIds.add(String(lane.id));
    if (lane.capacity !== 'serial' && lane.capacity !== 'parallel') {
      errors.push({ kind: 'lane-capacity-invalid', id: String(lane.id) });
    }
  }

  const order = sequence(horizon);
  const counted = new Set();
  const reported = new Set();
  for (const { id } of order) {
    if (counted.has(id) && !reported.has(id)) {
      errors.push({ kind: 'duplicate-id', id });
      reported.add(id);
    }
    counted.add(id);
  }

  const earlier = new Set();
  for (const { kind, item, id } of order) {
    if (kind === 'entry') {
      if (!isWeek(item.duration)) errors.push({ kind: 'duration-invalid', id });
      for (const field of ['notBefore', 'pinnedStart']) {
        if (isSet(item[field]) && !isWeek(item[field])) errors.push({ kind: 'week-invalid', id, ref: field });
      }
      for (const laneId of ids(item.lanes)) {
        if (!laneIds.has(laneId)) errors.push({ kind: 'lane-unknown', id, ref: laneId });
      }
    } else {
      const fixed = isSet(item.week);
      const derived = ids(item.after).length > 0;
      if (fixed && derived) errors.push({ kind: 'milestone-week-and-after', id });
      if (!fixed && !derived) errors.push({ kind: 'milestone-unanchored', id });
      if (fixed && !isWeek(item.week)) errors.push({ kind: 'week-invalid', id, ref: 'week' });
    }

    for (const ref of ids(item.after)) {
      if (!counted.has(ref)) errors.push({ kind: 'dependency-unknown', id, ref });
      else if (!earlier.has(ref)) errors.push({ kind: 'dependency-not-earlier', id, ref });
    }
    earlier.add(id);
  }

  return errors;
}

// ── Week ↔ calendar ───────────────────────────────────────────────────
//
// All arithmetic is on UTC calendar days, so a Horizon reads the same in
// every time zone and across daylight-saving changes. A 'YYYY-MM-DD' string
// means that day; a Date means its UTC calendar day.

function utcDay(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  }
  const date = value instanceof Date ? value : new Date(value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** The Monday that starts `week`. */
function weekStart(anchorDate, week) {
  return new Date(utcDay(anchorDate) + (week - 1) * WEEK_MS);
}

/** The Friday that ends `week` — what push-dates writes as a task's due date. */
function weekFriday(anchorDate, week) {
  return new Date(utcDay(anchorDate) + (week - 1) * WEEK_MS + 4 * DAY_MS);
}

/** The week containing `date`; days before the anchor give 0 or less. */
function weekOf(anchorDate, date) {
  return Math.floor((utcDay(date) - utcDay(anchorDate)) / WEEK_MS) + 1;
}

export { schedule, validate, weekStart, weekFriday, weekOf };
